import LangSwitcher from '@components/lang-switcher';
import { useAppSelector } from '@hooks/app/redux';
import useEnvService from '@hooks/app/useEnvService';
import useIsCompact from '@hooks/app/useIsCompact';
import useProviderStore from '@hooks/app/useProviderStore';
import useUsersProviders from '@hooks/query/provider/useUsersProviders';
import { Select } from '@ui';
import React from 'react';
import { Link } from 'react-router-dom';
import TopbarUser from './topbar-user';
import { TopbarSC } from './topbar.styled';

interface TopbarProps {
  showProviders?: boolean;
  showLangSwitcher?: boolean;
  authorized?: boolean;
}

const Topbar: React.FC<TopbarProps> = ({
  showLangSwitcher = true,
  showProviders = false,
  authorized = true,
}) => {
  const { data: providers, isLoading } = useUsersProviders();
  const isCompact = useIsCompact();

  const currentProvider = useAppSelector((state) => state.users.currentProvider);
  const { setCurrentProvider } = useProviderStore();

  const currentlySelected = React.useMemo(
    () => ({
      value: currentProvider?.id,
      label: currentProvider?.name,
    }),
    [currentProvider],
  );

  const options = React.useMemo(
    () =>
      providers
        ?.filter((p) => p.provider.status === 'approved' && p.status === 'approved')
        .map((p) => ({ label: p.provider.name, value: p.provider.id })),
    [providers],
  );

  const onProviderChange = React.useCallback(
    (v?: { label: string; value: string } | null) => {
      const approvedProvider = providers?.find((p) => p.provider.id === v?.value);

      if (!approvedProvider) {
        return;
      }

      setCurrentProvider(approvedProvider);
    },
    [providers],
  );

  return (
    <TopbarSC>
      <div>
        <Link
          to="/"
          className="tb-logo"
          style={isCompact ? { minWidth: 0, marginRight: 16 } : { minWidth: 260 }}
        >
          <img
            src={currentProvider?.logoUrl ?? require('../../assets/svg/logo.svg').default}
            width="auto"
            height="40"
            alt="logo"
          />
        </Link>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          {showProviders ? (
            <Select
              outlined
              options={options}
              value={currentlySelected as any}
              isLoading={isLoading}
              onChange={onProviderChange}
              isDisabled={process.env.NODE_ENV !== 'development'}
              className="provider-select"
            />
          ) : null}
        </div>
        {isCompact || !showLangSwitcher ? null : (
          <LangSwitcher theme="authorized" className="lang-switcher" />
        )}
      </div>

      {isCompact ? <div style={{ marginRight: 16 }} /> : <TopbarUser authorized={authorized} />}
    </TopbarSC>
  );
};

export default React.memo(Topbar);
